import React from 'react';
import { motion } from 'framer-motion';
import { SlideLayout } from '../SlideLayout';
import { Layers, ArrowRight, Clock, Gauge } from 'lucide-react';
export function StreamingArchSlide() {
  return <SlideLayout slideNumber={11} totalSlides={20} title="Architecture" data-id="element-483">
      <div className="w-full h-full flex flex-col" data-id="element-484">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-5" data-id="element-485">
          <h2 className="text-4xl md:text-5xl font-bold mb-2" data-id="element-486">
            Streaming, Not <span className="text-chip-gpu" data-id="element-487">Batching</span>
          </h2>
          <p className="text-2xl text-brand-muted max-w-3xl mx-auto" data-id="element-488">
            GPUs wait for data to fill buffers. FPGAs process element-by-element
            as data arrives.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 flex-1 mb-4" data-id="element-489">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2
        }} className="bg-brand-surface border border-brand-border rounded-2xl p-7 flex flex-col justify-between" data-id="element-490">
            <div data-id="element-491">
              <h3 className="text-2xl font-bold text-chip-gpu mb-5" data-id="element-492">
                GPU: Batch Processing
              </h3>
              <div className="space-y-4" data-id="element-493">
                {[{
                label: 'Wait for batch...'
              }, {
                label: 'Process batch'
              }, {
                label: 'Wait again...'
              }].map((step, i) => <div key={i} className="flex items-center gap-3" data-id="element-494">
                    <div className="w-10 h-10 rounded bg-chip-gpu/20 flex items-center justify-center text-chip-gpu font-mono text-base shrink-0" data-id="element-495">
                      {i + 1}
                    </div>
                    <div className="flex-1 h-4 bg-chip-gpu/10 rounded-full overflow-hidden" data-id="element-496">
                      {i !== 1 && <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: '100%'
                  }} transition={{
                    delay: i === 0 ? 0.5 : 2.2,
                    duration: 1.5
                  }} className="h-full bg-chip-gpu/30 rounded-full" data-id="element-497" />}
                      {i === 1 && <div className="h-full w-full bg-chip-gpu/40 rounded-full" data-id="element-498" />}
                    </div>
                    <span className="text-base text-brand-muted font-mono w-32 shrink-0" data-id="element-499">
                      {step.label}
                    </span>
                  </div>)}
              </div>
            </div>
            <p className="text-xl text-brand-muted mt-5" data-id="element-500">
              Latency penalty from queuing. Poor utilization at low concurrency.
              Hardware designed for batch dimension.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3
        }} className="bg-brand-surface border border-brand-primary/30 rounded-2xl p-7 relative overflow-hidden flex flex-col justify-between" data-id="element-501">
            <div className="absolute top-0 left-0 w-full h-1 bg-brand-primary" data-id="element-502" />
            <div data-id="element-503">
              <h3 className="text-2xl font-bold text-brand-primary mb-5" data-id="element-504">
                FPGA: Streaming Architecture
              </h3>
              <div className="space-y-4" data-id="element-505">
                {[1, 2, 3, 4, 5].map(i => <motion.div key={i} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: i * 0.15 + 0.4
              }} className="flex items-center gap-3" data-id="element-506">
                    <div className="w-5 h-5 rounded-full bg-brand-primary shrink-0" data-id="element-507" />
                    <ArrowRight size={16} className="text-brand-primary/50 shrink-0" data-id="element-508" />
                    <div className="flex-1 h-4 bg-brand-primary/30 rounded-full" data-id="element-509" />
                    <ArrowRight size={16} className="text-brand-primary/50 shrink-0" data-id="element-510" />
                    <div className="w-5 h-5 rounded-full bg-brand-primary/60 shrink-0" data-id="element-511" />
                  </motion.div>)}
              </div>
            </div>
            <p className="text-xl text-brand-muted mt-5" data-id="element-512">
              Data processed element-by-element at wire speed. No queuing delay.
              No minimum batch size for cost effectiveness.
            </p>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.7
      }} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-513">
          {[{
          icon: Clock,
          stat: 'Nanosecond',
          label: 'Deterministic latency',
          desc: 'No jitter, no scheduling overhead'
        }, {
          icon: Gauge,
          stat: 'Wire Speed',
          label: 'Processing rate',
          desc: 'No minimum batch size for cost effectiveness.'
        }, {
          icon: Layers,
          stat: '100%',
          label: 'Utilization at batch=1',
          desc: "vs GPU's chronic underutilization"
        }].map(item => <div key={item.label} className="bg-brand-dark border border-brand-border rounded-xl p-4 text-center" data-id="element-514">
              <div className="mx-auto mb-2 p-2 bg-brand-primary/10 w-fit rounded-lg text-brand-primary" data-id="element-515">
                <item.icon size={24} data-id="element-516" />
              </div>
              <div className="text-xl font-mono font-bold text-brand-primary" data-id="element-517">
                {item.stat}
              </div>
              <div className="text-lg font-bold text-white" data-id="element-518">{item.label}</div>
              <div className="text-base text-brand-muted mt-1" data-id="element-519">{item.desc}</div>
            </div>)}
        </motion.div>
        <p className="text-base text-brand-muted text-center mt-3 opacity-60" data-id="element-520">
          Sources: [23][24][25]
        </p>
      </div>
    </SlideLayout>;
}